import Papa from "papaparse";

// What the Customer → Searches page shows, as plain functions the page and
// its tests share. The server sends days as YYYY-MM-DD (Riyadh days) and
// times as UTC ISO strings.

export const STORE_URL = "https://nysmah.com";
export const STORE_TIME_ZONE = "Asia/Riyadh";

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7_days", label: "Last 7 days" },
  { key: "last_30_days", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ARABIC = /[؀-ۿ]/;

const parts = (day) => day.split("-").map(Number);

/** "7 Oct" for "2026-10-07". */
export function formatDay(day) {
  const [, month, date] = parts(day);
  return `${date} ${MONTHS[month - 1]}`;
}

/** "7 – 13 Oct 2026", "13 Oct 2026", "14 Sep – 13 Oct 2026" or "28 Dec 2025 – 3 Jan 2026". */
export function formatRange(start, end) {
  if (!start || !end) return "";
  const [startYear, startMonth, startDay] = parts(start);
  const [endYear, endMonth] = parts(end);
  const last = `${formatDay(end)} ${endYear}`;
  if (start === end) return last;
  if (startYear === endYear && startMonth === endMonth) return `${startDay} – ${last}`;
  return `${formatDay(start)}${startYear === endYear ? "" : ` ${startYear}`} – ${last}`;
}

/** A number against the period before: { text: "+12%", direction: "up" | "down" | "flat" }, or null with nothing to compare. */
export function percentChange(now, before) {
  if (!before) return now ? { text: "new", direction: "up" } : null;
  const percent = Math.round(((now - before) * 100) / before);
  if (percent === 0) return { text: "same", direction: "flat" };
  return { text: `${percent > 0 ? "+" : "−"}${Math.abs(percent)}%`, direction: percent > 0 ? "up" : "down" };
}

/** A word's searches against the period before, as a table shows it: "new", "–", "↑ 3" or "↓ 2". */
export function countChange(now, before) {
  if (!before) return "new";
  if (now === before) return "–";
  return now > before ? `↑ ${now - before}` : `↓ ${before - now}`;
}

/** ``part`` as a share of ``whole``: "12%", "<1%" for a little, "0%" for none. */
export function shareOf(part, whole) {
  if (!whole || !part) return "0%";
  const percent = Math.round((part * 100) / whole);
  return percent === 0 ? "<1%" : `${percent}%`;
}

/** The live store's results page for ``term``, in Arabic for an Arabic word. */
export function storeLink(term) {
  const locale = ARABIC.test(term) ? "ar" : "en";
  return `${STORE_URL}/${locale}/productlist?q=${encodeURIComponent(term)}`;
}

const riyadhDay = (date) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: STORE_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" })
    .format(date);

/** When a word was last searched, in Riyadh time: "Today, 14:20", "Yesterday, 09:05" or "11 Oct, 14:20". */
export function lastSearched(iso, now = new Date()) {
  if (!iso) return "";
  const when = new Date(iso);
  const clock = new Intl.DateTimeFormat("en-GB", {
    timeZone: STORE_TIME_ZONE, hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(when);
  const day = riyadhDay(when);
  if (day === riyadhDay(now)) return `Today, ${clock}`;
  if (day === riyadhDay(new Date(now.getTime() - 86400000))) return `Yesterday, ${clock}`;
  return `${formatDay(day)}, ${clock}`;
}

/** The rows whose word contains ``text`` (any case). */
export function filterRows(rows, text) {
  const wanted = text.trim().toLowerCase();
  return wanted ? rows.filter((row) => row.term.toLowerCase().includes(wanted)) : rows;
}

/** ``rows`` sorted by ``value(row)``, the word breaking ties so the order never jumps. */
export function sortRows(rows, value, direction) {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (left !== right) return left < right ? -sign : sign;
    return a.term.localeCompare(b.term);
  });
}

/** A spreadsheet of ``rows`` with ``columns`` ([heading, value(row)]), opening right in Excel (BOM for Arabic). */
export function toCsv(rows, columns) {
  const csv = Papa.unparse({
    fields: columns.map(([heading]) => heading),
    data: rows.map((row) => columns.map(([, value]) => value(row))),
  });
  return `﻿${csv}`;
}
